/**
 * Richtet ein frisch geöffnetes, leeres Browserfenster (die Hülle
 * `/browserfenster.html`) für einen KMVS-Fensterinhalt ein: dieselben Stylesheets wie das Hauptfenster,
 * Titel, Symbol und ein Gerüst aus Kopfzeile und Inhaltsfläche.
 *
 * Die Stylesheets laufen mit: Lädt das Hauptfenster später weitere (Vite lädt
 * CSS je Seite nach), kommen sie auch hier an. Die `makeStyles`-Styles und das
 * Fluent-Theme gehören NICHT dazu — die schreibt der eigene Renderer des
 * Browserfensters selbst hinein (`BrowserFensterHuelle`).
 */

/** Styles, die je Dokument eigens erzeugt werden und deshalb nicht kopiert werden. */
const istEigenerStil = (el: Element) =>
  el.hasAttribute("data-make-styles-bucket") ||
  el.hasAttribute("data-make-styles-rehydration") ||
  (el.id ?? "").startsWith("fui-FluentProvider");

const istStylesheet = (el: Element) =>
  (el.tagName === "LINK" && /\bstylesheet\b/i.test(el.getAttribute("rel") ?? "")) ||
  (el.tagName === "STYLE" && !istEigenerStil(el));

const istSymbol = (el: Element) => el.tagName === "LINK" && /\bicon\b/i.test(el.getAttribute("rel") ?? "");

function kopie(el: Element, ziel: Document): Element {
  if (el.tagName === "LINK") {
    const l = ziel.createElement("link");
    for (const a of Array.from(el.attributes)) l.setAttribute(a.name, a.value);
    // Absolute Adresse — unabhängig davon, wo die Hülle liegt.
    l.setAttribute("href", (el as HTMLLinkElement).href);
    return l;
  }
  const s = ziel.createElement("style");
  for (const a of Array.from(el.attributes)) s.setAttribute(a.name, a.value);
  s.textContent = stilText(el as HTMLStyleElement);
  return s;
}

/** Text eines `<style>` — auch wenn seine Regeln per `insertRule` statt als Text kamen. */
function stilText(el: HTMLStyleElement): string {
  if (el.textContent) return el.textContent;
  try {
    return Array.from(el.sheet?.cssRules ?? [])
      .map((r) => r.cssText)
      .join("\n");
  } catch {
    return "";
  }
}

export type PopupGeruest = {
  /** Ziel für die Kopfzeile (Titel, „Zurück in KMVS“). */
  kopf: HTMLElement;
  /** Ziel für den Fensterinhalt — entspricht dem Körper des schwebenden Fensters. */
  koerper: HTMLElement;
  /** Stoppt das Mitlaufen der Stylesheets. */
  aufraeumen: () => void;
};

export function richtePopupEin(popup: Window, titel: string, hintergrund: string): PopupGeruest {
  const dok = popup.document;
  dok.documentElement.lang = document.documentElement.lang || "de";
  dok.documentElement.setAttribute("translate", "no");
  // Ein wiederverwendetes Browserfenster (gleicher Name) bringt Reste mit.
  dok.head.replaceChildren();
  dok.body.replaceChildren();
  dok.title = titel;

  const meta = (name: string, content: string) => {
    const m = dok.createElement("meta");
    m.setAttribute("name", name);
    m.setAttribute("content", content);
    dok.head.appendChild(m);
  };
  const charset = dok.createElement("meta");
  charset.setAttribute("charset", "utf-8");
  dok.head.appendChild(charset);
  meta("color-scheme", "light");
  meta("viewport", "width=device-width, initial-scale=1");

  const kopien = new Map<Element, Element>();
  const uebernehmen = (el: Element) => {
    if (kopien.has(el) || !(istStylesheet(el) || istSymbol(el))) return;
    const k = kopie(el, dok);
    kopien.set(el, k);
    dok.head.appendChild(k);
  };
  Array.from(document.head.children).forEach(uebernehmen);

  const beobachter = new MutationObserver((aenderungen) => {
    for (const a of aenderungen) {
      if (a.type === "childList" && a.target === document.head) {
        a.addedNodes.forEach((n) => n.nodeType === 1 && uebernehmen(n as Element));
        a.removedNodes.forEach((n) => {
          const k = kopien.get(n as Element);
          if (k) {
            k.remove();
            kopien.delete(n as Element);
          }
        });
        continue;
      }
      // Text eines <style> geändert (Vite tauscht im Entwicklungsmodus so aus).
      const stil = a.target.nodeType === 1 ? (a.target as Element) : a.target.parentElement;
      const k = stil ? kopien.get(stil) : undefined;
      if (stil && k && stil.tagName === "STYLE") k.textContent = stilText(stil as HTMLStyleElement);
    }
  });
  beobachter.observe(document.head, { childList: true, subtree: true, characterData: true });

  Object.assign(dok.body.style, { margin: "0", height: "100vh", overflow: "hidden", background: hintergrund });
  const huelle = dok.createElement("div");
  huelle.setAttribute("data-kmvs-browserfenster", "");
  Object.assign(huelle.style, { display: "flex", flexDirection: "column", height: "100%" });
  const kopf = dok.createElement("div");
  kopf.setAttribute("data-kmvs-browserfenster-kopf", "");
  Object.assign(kopf.style, { flexShrink: "0" });
  // Wie der Körper des schwebenden Fensters (FloatingWindow `body`).
  const koerper = dok.createElement("div");
  koerper.setAttribute("data-kmvs-browserfenster-koerper", "");
  Object.assign(koerper.style, {
    flexGrow: "1",
    minHeight: "0",
    overflow: "auto",
    position: "relative",
    background: hintergrund,
  });
  huelle.append(kopf, koerper);
  dok.body.appendChild(huelle);

  return { kopf, koerper, aufraeumen: () => beobachter.disconnect() };
}
